import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

/** Response from get_task_message_thread_init: either existing thread_id or init data to create thread. */
export type TaskThreadInit =
  | { thread_id: string }
  | { property_id: string; task_id: string; participants: string[] };

// Fetch thread for a task
export const useTaskThread = (taskId: string | undefined) => {
  return useQuery({
    queryKey: ["message-thread", "task", taskId],
    queryFn: async () => {
      if (!taskId) return null;
      const { data, error } = await supabase
        .from("message_threads")
        .select("*")
        .eq("task_id", taskId)
        .eq("type", "task")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!taskId,
  });
};

/** Get existing task thread id or init data (participants) so client can create thread. */
export const useGetOrCreateTaskThread = (taskId: string | undefined, open: boolean) => {
  const queryClient = useQueryClient();
  const taskThread = useTaskThread(taskId);

  const createMutation = useMutation({
    mutationFn: async (init: { property_id: string; task_id: string; participants: string[] }) => {
      const { data, error } = await supabase
        .from("message_threads")
        .insert({
          type: "task",
          property_id: init.property_id,
          task_id: init.task_id,
          participants: init.participants,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["message-thread", "task", variables.task_id] });
      queryClient.invalidateQueries({ queryKey: ["inbox-threads"] });
    },
  });

  return {
    thread: taskThread.data,
    isLoading: taskThread.isLoading,
    error: taskThread.error,
    createMutation,
    refetch: taskThread.refetch,
  };
};

// Fetch thread for a property
export const usePropertyThread = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["message-thread", "property", propertyId],
    queryFn: async () => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from("message_threads")
        .select("*")
        .eq("property_id", propertyId)
        .eq("type", "property")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!propertyId,
  });
};

// Fetch messages for a thread with real-time updates
export const useMessages = (threadId: string | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["messages", threadId],
    queryFn: async () => {
      if (!threadId) return [];
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!threadId,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!threadId) return;

    const channel = supabase
      .channel(`messages-${threadId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `thread_id=eq.${threadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["messages", threadId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, queryClient]);

  return query;
};

// Send a message
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      threadId,
      messageText,
      attachments,
    }: {
      threadId: string;
      messageText: string;
      attachments?: string[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("messages")
        .insert({
          thread_id: threadId,
          message_text: messageText,
          sender_user_id: user.id,
          attachments: attachments || [],
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["messages", variables.threadId] });
    },
  });
};

// Upload attachment
export const useUploadAttachment = () => {
  return useMutation({
    mutationFn: async ({ file, threadId }: { file: File; threadId: string }) => {
      const fileExt = file.name.split(".").pop();
      const fileName = `${threadId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("cleaning-photos")
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    },
  });
};
